import React, { useLayoutEffect, useRef } from 'react';
import AnimatedWords from './AnimatedWords';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useCounterAnimation } from '../hooks/useScrollAnimation';

const Trust = () => {
  const containerRef = useRef(null);
  const paraRef = useRef(null);
  const { elementRef: statsRef, count } = useCounterAnimation(5);
  useLayoutEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: 'power2.out' },
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 75%',
          end: 'bottom 20%',
          toggleActions: 'play none none none',
          once: true
        }
      });

      const headingWords = containerRef.current?.querySelectorAll('h2 [data-animated-word]');
      if (headingWords && headingWords.length) {
        tl.fromTo(headingWords, { yPercent: 100 }, { yPercent: 0, duration: 0.5, ease: 'power3.out', stagger: 0.06, clearProps: 'transform' }, 0);
      }

      if (paraRef.current) {
        tl.fromTo(paraRef.current, { filter: 'blur(18px)', opacity: 0, y: 8 }, { filter: 'blur(0px)', opacity: 1, y: 0, duration: 0.6 }, '>-0.05');
      }

      const features = containerRef.current?.querySelectorAll('.trust-feature');
      if (features && features.length) {
        tl.fromTo(features, { filter: 'blur(18px)', opacity: 0, y: 10 }, { filter: 'blur(0px)', opacity: 1, y: 0, duration: 0.6, stagger: 0.12 }, '>-0.05');
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="trust" className="section trust" ref={containerRef}>
      <div className="container trust-grid">
        <div className="w-full lg:w-[90%] text-center lg:text-left">
          <div className="container section-badge flex items-center gap-3 text-[16px] md:text-[20px] !px-1">
            <div className="badge-dot"></div>
            People know us
          </div>
          <h2>
            <AnimatedWords auto={false} lines={["Delivering TRUST.", "Driving Growth."]} accentMap={[[1], []]} />
          </h2>
          <p ref={paraRef} className="muted mt-4 text-justify text-sm md:text-base lg:text-lg">We provide seamless, reliable, and efficient logistics solutions tailored to meet the dynamic needs of businesses. With advanced technology, a strong global network, and years of expertise, Timeline ensures every shipment arrives safely and on time.</p>

          <div className='flex flex-col lg:flex-row mt-20 gap-20'>
            <div className='flex flex-col items-center lg:items-start gap-5 trust-feature'>
              <div className="w-[64px] xs:w-[80px] sm:w-[90px] md:w-[108px]">
                <img
                  src="/images/smart_tracking_technology.webp"
                  alt="Smart Tracking Technology"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className='flex flex-col gap-5 leading-[1.1]'>
                <div className='text-base md:text-lg lg:text-xl flex lg:flex-col font-[700] gap-1 lg:gap-0 justify-center lg:justify-start'>
                  <p>Smart Tracking</p>
                  <p>Technology</p>
                </div>
                <div className='text-sm md:text-base lg:text-base leading-0'>
                  Real-time GPS visibility for complete transparency and peace of mind.
                </div>
              </div>
            </div>
            <div className='flex flex-col items-center lg:items-start gap-5 trust-feature'>
              <div className="w-[64px] xs:w-[80px] sm:w-[90px] md:w-[108px]">
                <img
                  src="/images/global_network_coverage.webp"
                  alt="Smart Tracking Technology"
                  className="w-full h-full object-contain"
                />
              </div>
              {/* <img src="/images/global_network_coverage.webp" className="object-fill" /> */}
              <div className='flex flex-col gap-5 leading-[1.1]'>
                <div className='text-base md:text-lg lg:text-xl flex lg:flex-col font-[700] gap-1 lg:gap-0 justify-center lg:justify-start'>
                  <p>Global Network</p>
                  <p>Coverage</p>
                </div>
                <div className='text-sm md:text-base lg:text-base leading-0'>
                  Wide coverage ensuring smooth and timely deliveries worldwide.
                </div>
              </div>
            </div>
          </div>
        </div>

        <figure className="w-fit trust-media flex justify-center items-center place-self-center">
          <img
            src="/images/trust_truck.webp"
            alt="Timeline Express truck"
            className="w-[250px] md:w-[400px] lg:w-[450px] h-auto mx-auto"
            loading="lazy"
            decoding="async"
          />
          <div ref={statsRef} className="stat-box trust-stat-badge">
            <h3 className="text-2xl md:text-3xl lg:text-4xl"><span className="years-num">{count}</span><span className="years-plus">+</span></h3>
            <p className="text-sm md:text-base lg:text-lg">Years of Excellence in Logistics</p>
          </div>
        </figure>


      </div>
    </section>
  );
};

export default Trust;
